import { AfterViewInit, Component, ElementRef, Input, ViewChild } from "@angular/core";
import { DynamicReportService } from "@app/main/components/reports/_services/dynamicReports.service";
import { ReportDataFunction } from "@app/main/components/reports/enums/ReportDataFunction";
import { ReportTableRowDataSource } from "@app/main/components/reports/enums/ReportTableRowDataSource";
import { IReportRow } from "@app/main/components/reports/models/IReportRow";
import { IDashboardWidget } from "@app/main/modules/dashboard/_models/IDashboardWidget";
import { ChartService } from "@app/services/charts.service";
import { ColourService } from "@app/services/colour.service";
import { ConstantsService } from "@app/services/constantsService";
import { UnitConversionService, UnitConversionType } from "@app/services/unitConversion.service";
import { ChartLegendPosition } from "../../_models/chart-legend-position";
import { DashboardService } from "../../dashboard.service";
import "./chart-widget.component.scss";

declare const Highcharts: any;

@Component({
    selector: "chart-widget-v3",
    template: require("./chart-widget.component.html")
})
export class ChartWidgetComponent implements AfterViewInit{
    @Input() widget: IDashboardWidget;

    @ViewChild("highchartContainer", { read: ElementRef }) highchartContainer: ElementRef;

    chart: Highcharts.ChartObject;
    chartConfig: Highcharts.Options;

    isMobile: boolean;
    log = false;

    constructor(
        private readonly constantsService: ConstantsService,
        private readonly chartService: ChartService,
        private readonly unitConversionService: UnitConversionService,
        private readonly colourService: ColourService,
        private readonly dashboardService: DashboardService,
        private readonly dynamicReportService: DynamicReportService
    ) { }

    ngAfterViewInit(): void {
        this.widget.onDataDefinitionUpdated = () => {
            this.isMobile = this.constantsService.getIsMobile();

            if (!this.widget.dataDefinition) {
                this.widget.dataDefinition = {};
            }

            this.dynamicReportService.filtersToDefinition({}, this.widget.dataDefinition);
            
            this.log = this.widget.ChartWidgetParameters.ChartType == "column";

            this.updateChartDefinition();
            this.updateSeriesData();
        }
    }

    // ---

    private updateChartDefinition() {
        this.chartConfig = this.chartService.createChartOptions(this.widget.ChartWidgetParameters.ChartType, this.widget.ChartWidgetParameters.ShowSeriesLegend);
        this.chartConfig.chart.backgroundColor = "transparent";
        this.chartConfig.chart.plotBackgroundColor = "transparent";

        this.chartConfig.plotOptions.series = this.chartConfig.plotOptions.series || {
            point: {
                events: {}
            }
        };

        if (!this.isMobile) {
            this.chartConfig.plotOptions.series.point.events.click = (event: MouseEvent) => {
                // Find data name
                const p = (event as any).point;
                //const dataTitle = p.name || p.series.name;

                let initialDrillRow: IReportRow = null;
                if (this.widget.dataDefinition.RowDataSource == ReportTableRowDataSource.Day) {

                    this.widget.dataDefinition.TableDefinition.Rows.forEach(row => {
                        const rowTime = new Date(row.Cells[0].Data).getTime();
                        if (p.x == rowTime) {
                            initialDrillRow = row;
                        }
                    });
                }


                return true;
            };
        }

        this.setChartColours();

        switch (this.widget.ChartWidgetParameters.ChartType) {
            case "pie":
                this.createPieChartConfig();
                break;

            case "bar":
                this.createBarChartConfig();
                break;

            case "column":
                this.createColumnChartConfig();
                break;

            case "polar":
                this.createPolarChartConfig();
                break;
        }
    }

    private createPieChartConfig() {
        this.setLegendPosition(this.widget.ChartWidgetParameters.LegendPosition || "right");
    }

    private createBarChartConfig() {

        this.chartConfig.chart.type = "bar";

        this.setLegendPosition(this.widget.ChartWidgetParameters.LegendPosition || "right");
    }

    private createColumnChartConfig() {
        this.chartConfig.chart.type = "column";

        this.chartConfig.plotOptions.column = {
            stacking: "normal",
            pointPadding: 0,
            groupPadding: 0.05,
            borderWidth: 0,
            dataLabels: {
                enabled: false,
                color: '#FFFFFF'
            }
        };

        this.setLegendPosition(this.widget.ChartWidgetParameters.LegendPosition || "right");
    }

    private createPolarChartConfig() {

        this.chartConfig.chart.type = "areaspline";
        this.chartConfig.chart.polar = true;
        this.chartConfig.chart.spacing = [0, 0, 0, 0];

        this.chartConfig.plotOptions.areaspline = this.chartService.getDefaultAreaChartConfig(2);
    }

    private updateSeriesData() {
        if (!this.widget.dataDefinition) {
            return;
        }

        if (!this.chartConfig.chart.type) {
            this.updateChartDefinition();
        }

        const series = this.dashboardService.convertDataDefinitionToChartSeries(this.widget.dataDefinition, this.widget.ChartWidgetParameters.HideEmptySeries, this.widget.DataWidgetParameters.UseTotals);

        this.widget.noData = series.length === 0 || series.filter(s => s.data.length > 0).length === 0;

        if (this.widget.ChartWidgetParameters.ChartType == "pie") {
            // If we are a pie, then squash our columns
            var newDataObject: [string, number][] = [];

            series.forEach(s => {
                const value = s.data.reduce((a, b) => a + b[1], 0);

                newDataObject.push([s.name, Number(value.toFixed(2))]);
            });

            var unitType = this.unitConversionService.getConversionLabel(this.widget.ChartWidgetParameters.UnitConversionType);

            if (unitType.length > 0) {
                unitType = unitType[0].toUpperCase() + unitType.substring(1);
            }
            else {
                unitType = "Events"
            }

            this.chartConfig.series = [{ name: unitType, data: newDataObject }];
        }
        else if (this.widget.ChartWidgetParameters.ChartType == "polar") {
            this.chartConfig.xAxis = {
                categories: series.map(s => s.name),
                lineWidth: 0,
            };

            this.chartConfig.chart = {
                polar: true,
                type: 'areaspline'
            };

            this.chartConfig.yAxis = {
                lineWidth: 0,
                min: 0,
                max: 100
            }

            this.chartConfig.series = series;
            this.chartConfig.series = [{
                "name": "Total Score",
                "data": []
            }];

            series.map(s => {
                const average = this.dashboardService.aggregateData(s.data.map(x => x[1]), ReportDataFunction.Average);
                this.chartConfig.series[0].data.push(average);
            });
        }
        else {
            this.chartConfig.series = series;
        }

        this.setChartColours();
        
        setTimeout(() => { // chart container uses ngIf in HTML so we need to wait till it loads sometimes
            if (this.widget.noData === false) {
                this.chart = Highcharts.chart(this.highchartContainer.nativeElement, this.chartConfig);
            }
        });
    }

    private setChartColours() {
        if (this.widget.ChartWidgetParameters.ChartType == "column") {

            if (this.chartConfig.series) {
                this.chartConfig.series.forEach((series, i) => {
                    series.color = this.colourService.getVigetColor(i, this.chartConfig.series.length);
                });
            }
        }
        else if (this.widget.ChartWidgetParameters.ChartType == "pie") {
            const numDimensions = this.chartConfig.series && this.chartConfig.series[0] ? this.chartConfig.series[0].data.length : 3;

            this.chartConfig.colors = [];

            for (var i = 0; i < numDimensions; i++) {

                const colour = this.colourService.getVigetColor(i, numDimensions/*, colourOffset*/);

                this.chartConfig.colors.push(colour);
            }
        }
        else if (this.widget.ChartWidgetParameters.ChartType == "polar") {

            this.chartConfig.colors = [this.colourService.getVigetColor(3)];
        }
    }

    private setLegendPosition(position: ChartLegendPosition) {
        this.chartConfig.legend = this.chartConfig.legend || {};

        if (this.widget.ChartWidgetParameters.ChartType == "pie") {
            var this_ = this;

            this.chartConfig.legend.labelFormatter = function () {
                return this_.basicPercentageFormatter(this.name, this.y, this.percentage);
            }
        }

        if (position == "top") {
            this.chartConfig.legend.align = 'right';
            this.chartConfig.legend.verticalAlign = 'top';
            this.chartConfig.legend.layout = 'horizontal';
        }
        else if (position == "bottom") {
            this.chartConfig.legend.x = 0;
            this.chartConfig.legend.y = 0;
        }
        else if (position == "right") {

            this.chartConfig.legend.align = 'right';
            this.chartConfig.legend.verticalAlign = 'middle';
            this.chartConfig.legend.layout = 'vertical';
            this.chartConfig.legend.x = -10;
            this.chartConfig.legend.itemMarginBottom = 10;
            this.chartConfig.legend.padding = 0;

            var this_ = this;

            if (this.widget.ChartWidgetParameters.ChartType == "pie") {
                this.chartConfig.legend.labelFormatter = function () {
                    return this_.pieLabelFormatter(this.name, this.y, this.percentage);
                }
            }
            else {

                this.chartConfig.legend.labelFormatter = function () {
                    return this_.basicLabelFormatter(this.name, this.yData);
                }
            }
        }
    }

    private basicPercentageFormatter(name: string, value: number, percentage: number): string {
        return `<b>${name}</b> (${Math.round(percentage)}%)`;
    }

    private basicLabelFormatter(name: string, values: number[]/*, percentage: number*/): string {

        return `<b>${name}</b><br />
                <span class='text-muted font-size-11 '>Total: ${this.dashboardService.aggregateData(values, ReportDataFunction.Sum).toLocaleString()}</span><br / >`;
    }

    private pieLabelFormatter(name: string, value: number, percentage: number): string {

        var totalValue = `${value}`;
        var unitType = this.unitConversionService.getConversionLabel(this.widget.ChartWidgetParameters.UnitConversionType);

        if (this.widget.ChartWidgetParameters.UnitConversionType == UnitConversionType.FuelCapacity) {
            var adjustedValue = this.unitConversionService.convertToUserUnit(value, UnitConversionType.FuelCapacity);
            totalValue = `${adjustedValue.toLocaleString()} ${unitType}`;
        }
        else {
            totalValue = `${value} ${unitType}`;
        }

        return `<b>${name}</b> (${Math.round(percentage)}%)<br />
                <span class='text-muted font-size-11'>Total: ${totalValue}</span><br />`;
    }
}
